/** @format — Account onboarding helpers */

export const COMPLETE_PROFILE_PATH = "/auth/complete-profile";
export const SIGNUP_PATH = "/auth/signup";

export function normalizeAuthUser(user) {
  if (!user) return user;

  const emailVerified =
    user.emailVerified ??
    user.email_verified ??
    user.isEmailVerified ??
    user.is_email_verified;

  const phone = user.phone ?? user.phoneNumber ?? user.phone_number;
  const accountStatus = user.accountStatus ?? user.account_status;

  const normalized = {
    ...user,
    firstName: user.firstName ?? user.first_name,
    lastName: user.lastName ?? user.last_name,
  };

  if (emailVerified !== undefined && emailVerified !== null) {
    normalized.emailVerified = emailVerified;
    normalized.email_verified = emailVerified;
  }

  if (phone !== undefined && phone !== null && phone !== "") {
    normalized.phone = phone;
    normalized.phoneNumber = phone;
    normalized.phone_number = phone;
  }

  if (accountStatus !== undefined && accountStatus !== null) {
    normalized.accountStatus = accountStatus;
    normalized.account_status = accountStatus;
  }

  return normalized;
}

export function getAccountStatus(user) {
  if (!user) return null;
  return user.accountStatus ?? user.account_status ?? null;
}

export function isPendingProfile(user) {
  const status = getAccountStatus(user);
  if (status === "pending") return true;
  if (status === "active") return false;
  return false;
}

export function needsEmailVerification(user) {
  if (!user) return false;

  const verified =
    user.emailVerified ??
    user.email_verified ??
    user.isEmailVerified ??
    user.is_email_verified;

  // Existing/legacy login payloads do not always include an email-verification
  // field. Absence is not evidence that an established account is unverified.
  // New registrations explicitly return false while verification is pending.
  if (verified === undefined || verified === null) return false;
  if (typeof verified === "string") {
    return ["false", "0", "no"].includes(verified.trim().toLowerCase());
  }
  return verified === false || verified === 0;
}

export function needsPhoneCollection(user) {
  if (!user) return false;

  const status = getAccountStatus(user);
  if (status === "active" || status === "admin_managed") return false;

  const phone = user.phone ?? user.phoneNumber ?? user.phone_number;
  if (phone) return false;

  if (isPendingProfile(user)) return true;

  const hasPhoneField =
    Object.prototype.hasOwnProperty.call(user, "phone") ||
    Object.prototype.hasOwnProperty.call(user, "phoneNumber") ||
    Object.prototype.hasOwnProperty.call(user, "phone_number");

  // Like verification, a missing phone field can mean the login endpoint
  // returned a compact user object. Only require collection when the backend
  // explicitly marks the profile pending or explicitly returns an empty phone.
  if (!hasPhoneField) return false;
  return true;
}

export function isOnboardingIncomplete(user) {
  return needsEmailVerification(user) || needsPhoneCollection(user);
}

export function getPostAuthPath(user) {
  if (!user) return "/auth/login";
  if (needsEmailVerification(user)) return SIGNUP_PATH;
  if (needsPhoneCollection(user)) return COMPLETE_PROFILE_PATH;
  return "/home";
}
